// Vendored from the JETCAM DXF Viewer (src/parse/tokenizer.ts), 2026-09-02.
// Origin: C:/inetpub/wwwroot/Jetcost/dxfviewer — that project is untouched.
// Local edits are marked "dxfsplitter:". Do not reformat; keep diffable against origin.

import { type IssueCollector, type Token } from "./types.ts";

/**
 * ASCII DXF is a flat sequence of line pairs: a group code, then its value.
 * That is the whole format. Everything structural — sections, tables, blocks,
 * entities — is expressed as agreed-upon codes within that stream.
 *
 * The one thing we care about beyond the pairs themselves is *where they came
 * from*. Every off-the-shelf DXF parser throws the line numbers away, and the
 * Contents tab is built on them, so they are tracked from the first step.
 */

/** Value classes implied by the group code's numeric range. */
export type GroupValueKind = "string" | "float" | "int" | "bool" | "binary";

/**
 * Group code ranges, per the DXF reference. Only used to decide how a value
 * should be read; an out-of-range code is treated as a string so that unknown
 * vendor extensions survive into `raw` unmangled.
 */
export function groupValueKind(code: number): GroupValueKind {
  if (code >= 0 && code <= 9) return "string";
  if (code >= 10 && code <= 59) return "float";
  if (code >= 60 && code <= 79) return "int";
  if (code >= 90 && code <= 99) return "int";
  if (code === 100 || code === 102 || code === 105) return "string";
  if (code >= 110 && code <= 149) return "float";
  if (code >= 160 && code <= 179) return "int";
  if (code >= 210 && code <= 239) return "float";
  if (code >= 270 && code <= 289) return "int";
  if (code >= 290 && code <= 299) return "bool";
  if (code >= 300 && code <= 309) return "string";
  if (code >= 310 && code <= 319) return "binary";
  if (code >= 320 && code <= 369) return "string";
  if (code >= 370 && code <= 389) return "int";
  if (code >= 390 && code <= 399) return "string";
  if (code >= 400 && code <= 409) return "int";
  if (code >= 410 && code <= 419) return "string";
  if (code >= 420 && code <= 429) return "int";
  if (code >= 430 && code <= 439) return "string";
  if (code >= 440 && code <= 449) return "int";
  if (code >= 450 && code <= 459) return "int";
  if (code >= 460 && code <= 469) return "float";
  if (code >= 470 && code <= 479) return "string";
  if (code === 999) return "string";
  if (code >= 1000 && code <= 1009) return "string";
  if (code >= 1010 && code <= 1059) return "float";
  if (code >= 1060 && code <= 1070) return "int";
  if (code === 1071) return "int";
  return "string";
}

/** Split on any of CRLF, LF or a lone CR. Real files use all three. */
function splitLines(text: string): string[] {
  return text.split(/\r\n|\r|\n/);
}

export interface TokenizeResult {
  readonly tokens: readonly Token[];
  /** The source split into lines, 0-indexed. Line N of the file is `lines[N-1]`. */
  readonly lines: readonly string[];
}

/**
 * Turn the file text into group-code/value pairs.
 *
 * Malformed input never throws: a code line that is not a number is reported
 * and skipped, and a code line with no value line after it is reported and
 * dropped. Everything before the damage is kept.
 */
export function tokenize(text: string, issues: IssueCollector): TokenizeResult {
  const lines = splitLines(text);
  const tokens: Token[] = [];

  // A trailing newline produces one empty final element; it is not a line.
  let count = lines.length;
  if (count > 0 && lines[count - 1] === "") count -= 1;

  let i = 0;
  let malformedCodes = 0;

  while (i < count) {
    const lineNo = i + 1; // 1-based, and this is the *code* line
    const codeText = (lines[i] ?? "").trim();
    i += 1;

    if (codeText === "") continue; // stray blank line between pairs

    const code = Number(codeText);
    if (!Number.isInteger(code)) {
      malformedCodes += 1;
      // Report the first few individually, then stop shouting.
      if (malformedCodes <= 5) {
        issues.add(
          "malformed-group-code",
          "error",
          `Expected a group code, found ${JSON.stringify(codeText.slice(0, 40))}.`,
          { line: lineNo },
        );
      }
      continue;
    }

    if (i >= count) {
      issues.add("truncated-token", "error", `Group code ${code} has no value line; file ends here.`, {
        line: lineNo,
      });
      break;
    }

    // The value line is taken as-is apart from its terminator. Trailing spaces
    // are significant in string values, so only numeric values get trimmed —
    // and that happens at read time, not here.
    tokens.push({ code, value: lines[i] ?? "", line: lineNo });
    i += 1;
  }

  if (malformedCodes > 5) {
    issues.add(
      "malformed-group-code",
      "error",
      `${malformedCodes} malformed group code lines in total; only the first 5 are listed individually.`,
    );
  }

  return { tokens, lines };
}

/** Read a token's value as a float, reporting rather than throwing on junk. */
export function asFloat(t: Token, issues: IssueCollector): number | null {
  const n = Number(t.value.trim());
  if (!Number.isFinite(n)) {
    issues.add("non-numeric-value", "error", `Group ${t.code} expected a number, found ${JSON.stringify(t.value.slice(0, 40))}.`, {
      line: t.line + 1,
    });
    return null;
  }
  return n;
}

/** Read a token's value as an integer. Accepts `1.0` as 1, which real files emit. */
export function asInt(t: Token, issues: IssueCollector): number | null {
  const n = asFloat(t, issues);
  if (n === null) return null;
  return Math.trunc(n);
}

export const DEG_TO_RAD = Math.PI / 180;
